import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Paper,
  Alert, LinearProgress, Button, Skeleton
} from '@mui/material';
import {
  Memory as CorePlatformIcon,
  Hub as ThirdPartyIcon,
  Visibility as ObservabilityIcon,
  Article as ArticleIcon,
  CheckCircleOutline as DoneIcon,
  RadioButtonUnchecked as PendingIcon,
  ManageAccounts as SmIcon,
  HealthAndSafety as ReliabilityIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';

const SM_COLOR = '#7c3aed';

const PRIMARY = '#0f1f3d';
const MUTED = '#64748b';
const SURFACE = '#f1f5fb';

const PILLAR_META = {
  corePlatform:  { label: 'Core Platform & Runtime Health',   icon: CorePlatformIcon,  color: '#1d6fd4' },
  thirdParty:    { label: 'Third-Party Integration Reliability', icon: ThirdPartyIcon,  color: '#e53935' },
  observability: { label: 'Safe Delivery',    icon: ObservabilityIcon, color: '#f4511e' },
};

const PillarCard = ({ pillarKey, data, onClick }) => {
  const meta = PILLAR_META[pillarKey];
  const cr          = data?.avgCompletionRate    || 0;
  const totalF      = data?.totalFeatures        || 0;
  const completedF  = data?.completedFeatures    ?? null;
  const totalSP     = data?.totalStoryPoints     || 0;
  const completedSP = data?.completedStoryPoints || 0;
  const configured  = data?.configured !== false;
  const Icon        = meta.icon;

  return (
    <Card
      elevation={0}
      onClick={() => onClick(pillarKey)}
      role="button"
      tabIndex={0}
      aria-label={`${meta.label}: ${cr}% complete, ${totalF} features, ${completedSP} of ${totalSP} story points`}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick(pillarKey)}
      sx={{
        height: '100%', borderRadius: '20px', cursor: 'pointer', overflow: 'hidden',
        border: '1.5px solid rgba(0,0,0,0.07)',
        transition: 'all 0.22s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 20px 48px rgba(0,0,0,0.12), 0 0 0 2px ${meta.color}50`
        },
        '&:focus-visible': { outline: `3px solid ${meta.color}`, outlineOffset: 3 }
      }}
    >
      <Box sx={{ px: 2.5, py: 2, bgcolor: `${meta.color}0d`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          bgcolor: `${meta.color}1a`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <Icon sx={{ color: meta.color, fontSize: 24 }} />
        </Box>
        <Typography variant="body1" fontWeight={600} color="#1a1a1a" lineHeight={1.35} sx={{ flex: 1 }}>
          {meta.label}
        </Typography>
      </Box>

      <CardContent sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
            <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: meta.color, lineHeight: 1 }}>
              {cr}%
            </Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500}>
              {configured ? 'complete' : 'not configured'}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={cr}
            aria-label={`${cr}% complete`}
            sx={{
              height: 8, borderRadius: 4,
              bgcolor: `${meta.color}18`,
              '& .MuiLinearProgress-bar': { bgcolor: meta.color, borderRadius: 4 }
            }}
          />
        </Box>

        <Box display="flex" gap={1.5}>
          <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: SURFACE, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a" lineHeight={1}>
              {completedF !== null ? completedF : totalF}
              {completedF !== null && (
                <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: MUTED }}>
                  /{totalF}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500} mt={0.25} display="block">
              features
            </Typography>
          </Box>
          <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: SURFACE, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a" lineHeight={1}>
              {completedSP}
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: MUTED }}>
                /{totalSP}
              </Typography>
            </Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500} mt={0.25} display="block">
              story pts
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const DONE_STATES = ['Closed', 'Done', 'Completed'];
const ACTIVE_STATES = ['Active', 'In Progress'];

const SmCard = ({ data, onClick }) => {
  const features    = data?.features || [];
  const total       = features.length;
  const done        = features.filter(f => DONE_STATES.includes(f.state)).length;
  const active      = features.filter(f => ACTIVE_STATES.includes(f.state)).length;
  const completionPct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Card
      elevation={0}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Service Management: ${completionPct}% complete, ${total} features`}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        height: '100%', borderRadius: '20px', cursor: 'pointer', overflow: 'hidden',
        border: '1.5px solid rgba(0,0,0,0.07)',
        transition: 'all 0.22s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 20px 48px rgba(0,0,0,0.12), 0 0 0 2px ${SM_COLOR}50`
        },
        '&:focus-visible': { outline: `3px solid ${SM_COLOR}`, outlineOffset: 3 }
      }}
    >
      <Box sx={{ px: 2.5, py: 2, bgcolor: `${SM_COLOR}0d`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          bgcolor: `${SM_COLOR}1a`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <SmIcon sx={{ color: SM_COLOR, fontSize: 24 }} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography variant="body1" fontWeight={600} color="#1a1a1a" lineHeight={1.2} noWrap>
            {data?.epicTitle || 'Service Management'}
          </Typography>
          {data?.adoProjectName && (
            <Typography variant="caption" color={MUTED} noWrap display="block">
              {data.adoProjectName}
            </Typography>
          )}
        </Box>
      </Box>

      <CardContent sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
            <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: SM_COLOR, lineHeight: 1 }}>
              {completionPct}%
            </Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500}>
              {total > 0 ? 'complete' : 'not configured'}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPct}
            aria-label={`${completionPct}% complete`}
            sx={{
              height: 8, borderRadius: 4,
              bgcolor: `${SM_COLOR}18`,
              '& .MuiLinearProgress-bar': { bgcolor: SM_COLOR, borderRadius: 4 }
            }}
          />
        </Box>

        <Box display="flex" gap={1.5}>
          <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: SURFACE, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a" lineHeight={1}>{total}</Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500} mt={0.25} display="block">features</Typography>
          </Box>
          <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: SURFACE, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a" lineHeight={1}>
              {done}
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: MUTED }}>/{total}</Typography>
            </Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500} mt={0.25} display="block">completed</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const ReliabilityProgressDashboard = ({ onPillarClick, onSmClick }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [smData, setSmData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [dashRes, smRes] = await Promise.all([
          get('/api/reliability/dashboard'),
          get('/api/reliability/service-management/data')
        ]);
        if (dashRes.success) setDashboardData(dashRes.data);
        else setError('Failed to load dashboard data');
        if (smRes.success) setSmData(smRes.data);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;

  if (loading) {
    return (
      <Box>
        {/* Hero skeleton */}
        <Skeleton variant="rounded" height={120} sx={{ mb: 3, borderRadius: '20px' }} />
        <Skeleton width={120} height={24} sx={{ mb: 2 }} />
        <Grid container spacing={2.5}>
          {[0, 1, 2].map(i => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: '20px' }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const configuredPillars = dashboardData ? Object.values(dashboardData).filter(p => p.configured) : [];
  const aggTotalSP     = configuredPillars.reduce((s, p) => s + (p.totalStoryPoints     || 0), 0);
  const aggCompletedSP = configuredPillars.reduce((s, p) => s + (p.completedStoryPoints || 0), 0);
  const aggTotalF      = configuredPillars.reduce((s, p) => s + (p.totalFeatures        || 0), 0);
  const overallCompletion = aggTotalSP > 0
    ? Math.round((aggCompletedSP / aggTotalSP) * 100)
    : configuredPillars.length > 0
      ? Math.round(configuredPillars.reduce((s, p) => s + (p.avgCompletionRate || 0), 0) / configuredPillars.length)
      : 0;

  return (
    <Box>
      {/* Hero card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px', overflow: 'hidden', mb: 3,
          border: '1.5px solid rgba(0,0,0,0.07)',
          background: 'linear-gradient(135deg, #0f1f3d 0%, #1a3460 55%, #162e52 100%)'
        }}
      >
        <Box sx={{ px: 4, py: 3.5, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box flex={1} minWidth={240}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Box sx={{
                width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <ReliabilityIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#fff" lineHeight={1.2}>
                  Reliability &amp; Scale Programme
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
                  Three-pillar platform resilience initiative
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArticleIcon />}
              component="a"
              href="/docs/HighQ-Reliability-Roadmap.html"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open Programme Overview document"
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                borderColor: 'rgba(255,255,255,0.5)', color: '#fff',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Programme Overview
            </Button>
          </Box>

          <Box display="flex" gap={1.5} flexWrap="wrap">
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '12px',
              px: 2.5, py: 2, textAlign: 'center', minWidth: 100
            }}>
              <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                {overallCompletion}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5, display: 'block' }}>
                overall completion
              </Typography>
            </Box>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '12px',
              px: 2.5, py: 2, textAlign: 'center', minWidth: 100
            }}>
              <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                {aggTotalF}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5, display: 'block' }}>
                total features
              </Typography>
            </Box>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '12px',
              px: 2.5, py: 2, textAlign: 'center', minWidth: 100
            }}>
              <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                {configuredPillars.length}/3
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5, display: 'block' }}>
                pillars active
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Pillar cards */}
      <Typography variant="subtitle1" fontWeight={600} color={PRIMARY} mb={2}>
        Pillar Progress
      </Typography>
      <Grid container spacing={2.5}>
        {Object.keys(PILLAR_META).map(key => (
          <Grid item xs={12} sm={6} lg={3} key={key}>
            <PillarCard pillarKey={key} data={dashboardData?.[key]} onClick={onPillarClick} />
          </Grid>
        ))}
      </Grid>

      {/* Service Management card */}
      {smData && (
        <Box mt={4}>
          <Typography variant="subtitle1" fontWeight={600} color={PRIMARY} mb={2}>
            Service Management
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} lg={3}>
              <SmCard data={smData} onClick={() => onSmClick && onSmClick()} />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ReliabilityProgressDashboard;
